import React, { useRef, useState } from "react";
import { observer } from "mobx-react";
import { Box, Grid2, useTheme } from "@mui/material";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import { PodRevenueDashboardStore } from "../../../stores/dashboards/PodRevenueDashboardStore";
import PodRevenueFilters from "../../components/dashboard/pod-revenue/PodRevenueFilters";
import PodRevenueKpis, { PodKpiTarget } from "../../components/dashboard/pod-revenue/PodRevenueKpis";
import PodRevenueMix, { PodMixKey } from "../../components/dashboard/pod-revenue/PodRevenueMix";
// Hidden for now — weekly gross trend UI + its data use.
// import PodRevenueTrend from "../../components/dashboard/pod-revenue/PodRevenueTrend";
import PodRevenueProductsTable from "../../components/dashboard/pod-revenue/PodRevenueProductsTable";
import PodRevenueLinesListing from "../../components/dashboard/pod-revenue/PodRevenueLinesListing";
import PodRevenueDrawer from "../../components/dashboard/pod-revenue/PodRevenueDrawer";

const PodRevenueDashboardView: React.FC = observer(() => {
  const [store] = useState(() => new PodRevenueDashboardStore());
  const theme = useTheme();
  const [activeMix, setActiveMix] = useState<PodMixKey | null>(null);
  const refs = {
    // trend: useRef<HTMLDivElement>(null), // weekly gross trend hidden for now
    products: useRef<HTMLDivElement>(null),
    lines: useRef<HTMLDivElement>(null),
  };

  // KPI click: jump to the matching section.
  const handleKpi = (target: PodKpiTarget) => {
    refs[target].current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: theme.palette.customColors.pageBackground,
        pb: 4,
      }}
    >
      <Grid2 size={{ xs: 12 }} sx={{ px: 2, pt: 2 }}>
        <LoaderOverlay
          loading={
            store.summaryLoading
          }
        />
        <PodRevenueFilters store={store} />
        <PodRevenueKpis store={store} onSelect={handleKpi} />
        <PodRevenueMix store={store} activeKey={activeMix} onActiveChange={setActiveMix} />
        {/* Weekly gross trend hidden for now — do not render / request separately.
        <Box ref={refs.trend} sx={{ scrollMarginTop: 16 }}>
          <PodRevenueTrend store={store} />
        </Box>
        */}
        <Box ref={refs.products} sx={{ scrollMarginTop: 16 }}>
          <PodRevenueProductsTable store={store} />
        </Box>
        <Box ref={refs.lines} sx={{ scrollMarginTop: 16 }}>
          <PodRevenueLinesListing store={store} />
        </Box>
        <PodRevenueDrawer store={store} />
      </Grid2>
    </Box>
  );
});

const PodRevenueDashboard: React.FC = () => {
  const { userStore } = useStores();

  if (
    !userStore.hasPermission([
      EmployeeRoles.VIEW_POD_REVENUE_DASHBOARD,
      EmployeeRoles.VIEW_SALES_DASHBOARD,
    ])
  ) {
    return <PermissionBlockLabel />;
  }

  return <PodRevenueDashboardView />;
};

export default observer(PodRevenueDashboard);
